import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Upload, Mail, TrendingUp, RefreshCw, MapPin, Search, Zap, StopCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Suchbegriff-Varianten für maximale Abdeckung (jede Variante bringt bis zu 60 neue Treffer)
const PFLEGE_KEYWORDS = [
  "ambulanter Pflegedienst",
  "Pflegedienst",
  "häusliche Krankenpflege",
  "Hauskrankenpflege",
  "Sozialstation",
];

// Top-52 Städte/Kreise für NRW-Vollscan (kreisfreie Städte + größte Kreise)
const NRW_CITIES = [
  "Köln", "Düsseldorf", "Dortmund", "Essen", "Duisburg", "Bochum", "Wuppertal", "Bielefeld",
  "Bonn", "Münster", "Mönchengladbach", "Gelsenkirchen", "Aachen", "Krefeld", "Oberhausen",
  "Hagen", "Hamm", "Mülheim an der Ruhr", "Leverkusen", "Solingen", "Herne", "Neuss",
  "Paderborn", "Bottrop", "Recklinghausen", "Remscheid", "Bergisch Gladbach", "Moers",
  "Siegen", "Gütersloh", "Witten", "Iserlohn", "Marl", "Lünen", "Velbert", "Minden",
  "Dorsten", "Ratingen", "Castrop-Rauxel", "Dormagen", "Herten", "Bocholt", "Detmold",
  "Grevenbroich", "Lüdenscheid", "Troisdorf", "Wesel", "Viersen", "Euskirchen", "Kleve",
  "Ahlen", "Kamen",
];

// Andere Bundesländer für spätere Vollscans
const CITIES_BY_BUNDESLAND = {
  "Nordrhein-Westfalen": NRW_CITIES,
  "Bayern": ["München", "Nürnberg", "Augsburg", "Regensburg", "Würzburg", "Ingolstadt", "Fürth", "Erlangen", "Bayreuth", "Bamberg", "Aschaffenburg", "Landshut", "Kempten", "Rosenheim", "Neu-Ulm", "Schweinfurt", "Passau", "Freising", "Straubing", "Dachau"],
  "Baden-Württemberg": ["Stuttgart", "Mannheim", "Karlsruhe", "Freiburg", "Heidelberg", "Heilbronn", "Ulm", "Pforzheim", "Reutlingen", "Ludwigsburg", "Tübingen", "Esslingen", "Villingen-Schwenningen", "Konstanz", "Sindelfingen", "Aalen", "Baden-Baden", "Böblingen"],
  "Niedersachsen": ["Hannover", "Braunschweig", "Osnabrück", "Oldenburg", "Wolfsburg", "Göttingen", "Salzgitter", "Hildesheim", "Delmenhorst", "Wilhelmshaven", "Lüneburg", "Celle", "Emden", "Cuxhaven", "Hameln"],
  "Hessen": ["Frankfurt am Main", "Wiesbaden", "Kassel", "Darmstadt", "Offenbach am Main", "Hanau", "Gießen", "Marburg", "Fulda", "Rüsselsheim", "Bad Homburg", "Wetzlar"],
  "Rheinland-Pfalz": ["Mainz", "Ludwigshafen", "Koblenz", "Trier", "Kaiserslautern", "Worms", "Neuwied", "Neustadt", "Speyer", "Frankenthal", "Bad Kreuznach", "Landau"],
  "Berlin": ["Berlin-Mitte", "Berlin-Charlottenburg", "Berlin-Neukölln", "Berlin-Kreuzberg", "Berlin-Spandau", "Berlin-Steglitz", "Berlin-Tempelhof", "Berlin-Reinickendorf", "Berlin-Pankow", "Berlin-Lichtenberg", "Berlin-Marzahn", "Berlin-Treptow"],
  "Hamburg": ["Hamburg-Altona", "Hamburg-Eimsbüttel", "Hamburg-Wandsbek", "Hamburg-Nord", "Hamburg-Mitte", "Hamburg-Bergedorf", "Hamburg-Harburg"],
};

/**
 * Admin · Outreach
 * ─────────────────
 * Kaltakquise-Steuerung für Pflege-KI Schnupper-Woche.
 *
 * Bulk-Import via CSV (Semikolon-getrennt, Header-Zeile):
 *   firmenname;ansprechpartner;email;telefon;ort;bundesland;website;notes
 *
 * Versand: pro Prospect kann Touch 1/2/3 einzeln oder in Bulk gesendet werden.
 */
export default function AdminOutreach() {
  const [stats, setStats] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [selectedTouch, setSelectedTouch] = useState({});  // {prospectId: touch}

  // Google Places · Recherche
  const [placesConfigured, setPlacesConfigured] = useState(null);  // null=unknown, true/false
  const [placesQuery, setPlacesQuery] = useState("ambulanter Pflegedienst");
  const [placesRegion, setPlacesRegion] = useState("Nordrhein-Westfalen");
  const [placesBusy, setPlacesBusy] = useState(false);
  const [placesResult, setPlacesResult] = useState(null);

  // Batch-Vollscan
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);  // {done, total, currentCity, importedTotal, foundTotal, dupTotal, noEmailTotal}
  const batchStopRef = useRef(false);

  // AOK · Pflegenavigator
  const [aokRegion, setAokRegion] = useState("Nordrhein-Westfalen");
  const [aokMax, setAokMax] = useState(200);
  const [aokBusy, setAokBusy] = useState(false);
  const [aokProgress, setAokProgress] = useState(null);  // {offset, total_imported, ...}
  const aokStopRef = useRef(false);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/outreach/stats`, { withCredentials: true }),
        axios.get(`${API}/outreach/prospects`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setProspects(p.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Laden fehlgeschlagen.");
    }
  };

  useEffect(() => { load(); }, []);

  // Prüfen, ob Google-Places-Key konfiguriert ist
  useEffect(() => {
    axios.get(`${API}/outreach/places/status`, { withCredentials: true })
      .then((r) => setPlacesConfigured(!!r.data?.configured))
      .catch(() => setPlacesConfigured(false));
  }, []);

  const runPlacesSearch = async () => {
    if (!placesQuery.trim() || placesBusy) return;
    setPlacesBusy(true);
    setPlacesResult(null);
    try {
      const { data } = await axios.post(
        `${API}/outreach/places/search`,
        {
          query: placesQuery.trim(),
          region: placesRegion.trim() || "Deutschland",
          max_pages: 3,
          auto_import: true,
        },
        { withCredentials: true, timeout: 120000 },
      );
      setPlacesResult(data);
      toast.success(`${data.imported} neue Prospects importiert · ${data.found} gefunden · ${data.duplicates_skipped} Duplikate · ${data.no_email_skipped} ohne E-Mail`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Places-Suche fehlgeschlagen.");
    } finally {
      setPlacesBusy(false);
    }
  };

  const runBatchScan = async () => {
    const cities = CITIES_BY_BUNDESLAND[placesRegion] || [];
    if (cities.length === 0) {
      toast.error("Für dieses Bundesland ist kein Vollscan konfiguriert.");
      return;
    }
    const totalCalls = cities.length * PFLEGE_KEYWORDS.length;
    const estCost = (totalCalls * 0.05).toFixed(2);  // grobe Schätzung
    const ok = window.confirm(
      `Vollscan ${placesRegion}:\n\n` +
      `${cities.length} Städte × ${PFLEGE_KEYWORDS.length} Suchbegriffe = ${totalCalls} API-Aufrufe\n` +
      `Geschätzte Dauer: ${Math.round(totalCalls * 15 / 60)} Minuten\n` +
      `Geschätzte Google-Kosten: ca. ${estCost} €\n\n` +
      `Fortsetzen?`
    );
    if (!ok) return;

    batchStopRef.current = false;
    setBatchRunning(true);
    setBatchProgress({ done: 0, total: totalCalls, currentCity: "", importedTotal: 0, foundTotal: 0, dupTotal: 0, noEmailTotal: 0 });

    let done = 0, importedTotal = 0, foundTotal = 0, dupTotal = 0, noEmailTotal = 0;

    outer: for (const city of cities) {
      for (const kw of PFLEGE_KEYWORDS) {
        if (batchStopRef.current) break outer;
        const query = `${kw} ${city}`;
        setBatchProgress({ done, total: totalCalls, currentCity: `${city} · ${kw}`, importedTotal, foundTotal, dupTotal, noEmailTotal });

        try {
          const { data } = await axios.post(
            `${API}/outreach/places/search`,
            { query, region: placesRegion, max_pages: 3, auto_import: true },
            { withCredentials: true, timeout: 120000 },
          );
          importedTotal += data.imported || 0;
          foundTotal += data.found || 0;
          dupTotal += data.duplicates_skipped || 0;
          noEmailTotal += data.no_email_skipped || 0;
        } catch (err) {
          console.warn(`Batch-Suche fehlgeschlagen für "${query}":`, err?.response?.data?.detail || err.message);
          // weiter machen, ein Fehlschlag darf nicht den ganzen Scan stoppen
        }
        done += 1;
        setBatchProgress({ done, total: totalCalls, currentCity: `${city} · ${kw}`, importedTotal, foundTotal, dupTotal, noEmailTotal });
      }
    }

    setBatchRunning(false);
    if (batchStopRef.current) {
      toast.info(`Vollscan abgebrochen. ${importedTotal} neue Prospects importiert.`);
    } else {
      toast.success(`Vollscan fertig! ${importedTotal} neue Prospects · ${foundTotal} Treffer gescannt · ${dupTotal} Duplikate · ${noEmailTotal} ohne E-Mail`);
    }
    load();
  };

  const stopBatch = () => { batchStopRef.current = true; };

  const runAokScan = async () => {
    const ok = window.confirm(
      `AOK-Vollscan ${aokRegion}:\n\n` +
      `Zieht bis zu ${aokMax} Pflegedienste aus dem AOK-Pflegenavigator.\n` +
      `Ca. Laufzeit: ${Math.round(aokMax / 10 * 15 / 60)} Min.\n\n` +
      `Vorteil: KEIN 60-Treffer-Limit wie Google. Deutlich mehr Coverage.\n\n` +
      `Fortsetzen?`
    );
    if (!ok) return;
    aokStopRef.current = false;
    setAokBusy(true);
    setAokProgress({ offset: 0, imported: 0, scanned: 0, matched: 0, dup: 0, noEmail: 0 });

    let offset = 0;
    let imported = 0, scanned = 0, matched = 0, dup = 0, noEmail = 0;

    // AOK-Scan läuft in Chunks (jeder ~30-60s), damit Cloudflare-Timeout nicht kickt
    while (matched < aokMax && !aokStopRef.current) {
      const chunkSize = Math.min(30, aokMax - matched);
      try {
        const { data } = await axios.post(
          `${API}/outreach/aok/scan`,
          { plz_prefix: aokRegion, max_results: chunkSize, from_offset: offset, auto_import: true },
          { withCredentials: true, timeout: 180000 },
        );
        imported += data.imported || 0;
        scanned += data.scanned || 0;
        matched += data.matched || 0;
        dup += data.duplicates_skipped || 0;
        noEmail += data.no_email_skipped || 0;
        offset = data.next_offset ?? (offset + data.scanned);
        setAokProgress({ offset, imported, scanned, matched, dup, noEmail });
        if (!data.next_offset || data.scanned === 0) break;
      } catch (err) {
        toast.error(err?.response?.data?.detail || "AOK-Scan fehlgeschlagen.");
        break;
      }
    }
    setAokBusy(false);
    if (aokStopRef.current) toast.info(`AOK-Scan abgebrochen. ${imported} neue Prospects.`);
    else toast.success(`AOK-Scan fertig! ${imported} neu · ${scanned} gescannt · ${matched} in Region · ${dup} Duplikate`);
    load();
  };
  const stopAokScan = () => { aokStopRef.current = true; };

  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const header = lines[0].split(";").map((s) => s.trim().toLowerCase());
    const idx = (k) => header.indexOf(k);
    return lines.slice(1).map((line) => {
      const cols = line.split(";").map((c) => c.trim());
      const get = (k) => (idx(k) >= 0 ? cols[idx(k)] || "" : "");
      return {
        firmenname: get("firmenname"),
        ansprechpartner: get("ansprechpartner"),
        email: get("email"),
        telefon: get("telefon"),
        ort: get("ort"),
        bundesland: get("bundesland"),
        website: get("website"),
        notes: get("notes"),
      };
    }).filter((p) => p.firmenname && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email));
  };

  const handleImport = async () => {
    const parsed = parseCsv(csvText);
    if (parsed.length === 0) {
      toast.error("Keine gültigen Zeilen gefunden. Format prüfen.");
      return;
    }
    setImporting(true);
    try {
      const { data } = await axios.post(
        `${API}/outreach/prospects/import`,
        { prospects: parsed },
        { withCredentials: true },
      );
      toast.success(`${data.inserted} neu importiert. ${data.skipped_duplicates} Duplikate übersprungen.`);
      setCsvText("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import fehlgeschlagen.");
    } finally {
      setImporting(false);
    }
  };

  const sendTouch = async (prospectId, touch) => {
    setSendingId(prospectId);
    try {
      await axios.post(
        `${API}/outreach/prospects/${prospectId}/send`,
        { touch: Number(touch) },
        { withCredentials: true },
      );
      toast.success(`Touch ${touch} versendet`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Versand fehlgeschlagen.");
    } finally {
      setSendingId(null);
    }
  };

  const getNextTouch = (p) => {
    const sent = (p.touches_sent || []).map((t) => t.touch);
    for (const t of [1, 2, 3]) if (!sent.includes(t)) return t;
    return null;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-outreach-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-sand-800">Outreach · Pflegedienste</h1>
          <p className="text-sand-600 text-sm mt-1">Kaltakquise für die Pflege-KI Schnupper-Woche (29 €)</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-btn"><RefreshCw className="h-4 w-4 mr-2"/>Neu laden</Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          <Stat label="Prospects" value={stats.prospects_total}/>
          <Stat label="Touch 1" value={stats.touched_1}/>
          <Stat label="Touch 2" value={stats.touched_2}/>
          <Stat label="Touch 3" value={stats.touched_3}/>
          <Stat label="Schnupper bezahlt" value={stats.schnupper_paid_all} accent="text-terra"/>
          <Stat label="Abmeldungen" value={stats.unsubscribed}/>
        </div>
      )}

      {/* AOK-Pflegenavigator · vollständige Deutschland-Datenbank */}
      <div className="rounded-2xl border-2 border-emerald-600/50 bg-emerald-50 p-6 mb-8" data-testid="aok-section">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="h-5 w-5 text-emerald-700"/>
          <h2 className="font-semibold text-sand-800 text-lg">AOK-Pflegenavigator · Vollscan</h2>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider ml-2">
            KEIN Limit
          </span>
        </div>
        <p className="text-sand-700 text-sm mb-4">
          Nutzt das offizielle AOK-Verzeichnis (17.111 Pflegedienste deutschlandweit).
          <b> Kein 60-Treffer-Limit</b> wie bei Google. Läuft alphabetisch durch, filtert per PLZ auf dein Bundesland.
          Kostenlos (kein API-Key nötig).
        </p>

        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div>
            <Label className="text-[12px] font-semibold text-sand-700 mb-1 block">Bundesland (PLZ-Filter)</Label>
            <select
              value={aokRegion}
              onChange={(e) => setAokRegion(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-1 text-sm"
              data-testid="aok-region-select"
            >
              <option value="">Deutschland (alle)</option>
              {Object.keys({"Nordrhein-Westfalen":1,"Bayern":1,"Baden-Württemberg":1,"Niedersachsen":1,"Hessen":1,"Rheinland-Pfalz":1,"Berlin":1,"Hamburg":1,"Schleswig-Holstein":1,"Brandenburg":1,"Sachsen":1,"Sachsen-Anhalt":1,"Thüringen":1,"Mecklenburg-Vorpommern":1,"Saarland":1,"Bremen":1}).map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[12px] font-semibold text-sand-700 mb-1 block">Max. Treffer (Zielregion)</Label>
            <select
              value={aokMax}
              onChange={(e) => setAokMax(Number(e.target.value))}
              className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-1 text-sm"
              data-testid="aok-max-select"
            >
              <option value={50}>50 (Test)</option>
              <option value={200}>200 (kleines Sample)</option>
              <option value={500}>500 (mittel)</option>
              <option value={1500}>1.500 (groß, ~40 Min.)</option>
              <option value={5000}>5.000 (Vollscan NRW)</option>
            </select>
          </div>
          <div className="flex items-end">
            {!aokBusy ? (
              <Button
                onClick={runAokScan}
                data-testid="aok-scan-btn"
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white h-10"
              >
                <Zap className="h-4 w-4 mr-2"/>AOK-Scan starten
              </Button>
            ) : (
              <Button
                onClick={stopAokScan}
                variant="outline"
                data-testid="aok-stop-btn"
                className="w-full border-red-400 text-red-700 hover:bg-red-50 h-10"
              >
                <StopCircle className="h-4 w-4 mr-2"/>Abbrechen
              </Button>
            )}
          </div>
        </div>

        {aokProgress && (
          <div className="mt-4 rounded-lg bg-white border border-emerald-200 p-4" data-testid="aok-progress">
            <div className="flex items-baseline justify-between mb-2">
              <div className="font-semibold text-sand-800 text-[14px]">
                {aokBusy ? "Läuft…" : "Ergebnis"} · Offset: {aokProgress.offset}
              </div>
              <div className="text-sand-500 text-[12px]">{Math.round((aokProgress.matched / aokMax) * 100)}%</div>
            </div>
            <div className="w-full bg-emerald-100 rounded-full h-2 overflow-hidden mb-3">
              <div className="bg-emerald-600 h-2 transition-all" style={{ width: `${Math.min(100, (aokProgress.matched / aokMax) * 100)}%` }} />
            </div>
            <div className="grid grid-cols-5 gap-2 text-[12.5px]">
              <div><span className="text-sand-500">Importiert:</span> <b className="text-emerald-700">{aokProgress.imported}</b></div>
              <div><span className="text-sand-500">Gescannt:</span> <b>{aokProgress.scanned}</b></div>
              <div><span className="text-sand-500">In Region:</span> <b>{aokProgress.matched}</b></div>
              <div><span className="text-sand-500">Duplikate:</span> {aokProgress.dup}</div>
              <div><span className="text-sand-500">Ohne E-Mail:</span> {aokProgress.noEmail}</div>
            </div>
          </div>
        )}
      </div>

      {/* Google Places · Pflegedienste finden */}
      <div className="rounded-2xl border-2 border-terra/40 bg-peach-50 p-6 mb-8" data-testid="places-search-section">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="h-5 w-5 text-terra"/>
          <h2 className="font-semibold text-sand-800 text-lg">Pflegedienste finden · Google Maps</h2>
        </div>

        {placesConfigured === false && (
          <div className="rounded-lg border border-red-300 bg-red-50 p-4 mb-4" data-testid="places-not-configured">
            <div className="font-semibold text-red-700 mb-1">Google Places API-Key nicht konfiguriert</div>
            <div className="text-sm text-red-700 leading-relaxed">
              Bitte in <code className="bg-white px-1 py-0.5 rounded border">backend/.env</code> die Variable{" "}
              <code className="bg-white px-1 py-0.5 rounded border">GOOGLE_PLACES_API_KEY</code> setzen.
              Anleitung:{" "}
              <a href="https://developers.google.com/maps/documentation/places/web-service/cloud-setup"
                 target="_blank" rel="noreferrer" className="underline">
                Places API Setup
              </a>{" "}
              → Places API (New) aktivieren → API-Key erstellen → im Backend hinterlegen.
            </div>
          </div>
        )}

        <p className="text-sand-700 text-sm mb-4">
          Sucht per Google Maps nach Pflegediensten in der gewählten Region,
          extrahiert automatisch E-Mails aus Impressum/Kontakt-Seiten und
          importiert Treffer direkt in die Prospect-Liste. Duplikate werden übersprungen.
        </p>

        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div>
            <Label className="text-[12px] font-semibold text-sand-700 mb-1 block">Suchbegriff</Label>
            <Input
              value={placesQuery}
              onChange={(e) => setPlacesQuery(e.target.value)}
              placeholder="ambulanter Pflegedienst"
              data-testid="places-query-input"
            />
          </div>
          <div>
            <Label className="text-[12px] font-semibold text-sand-700 mb-1 block">Region (Bundesland)</Label>
            <select
              value={placesRegion}
              onChange={(e) => setPlacesRegion(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-1 text-sm"
              data-testid="places-region-select"
            >
              <option value="">Deutschland (alle)</option>
              <option>Nordrhein-Westfalen</option>
              <option>Bayern</option>
              <option>Baden-Württemberg</option>
              <option>Niedersachsen</option>
              <option>Hessen</option>
              <option>Rheinland-Pfalz</option>
              <option>Berlin</option>
              <option>Hamburg</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button
              onClick={runPlacesSearch}
              disabled={placesBusy || placesConfigured === false || !placesQuery.trim()}
              data-testid="places-search-btn"
              className="w-full bg-terra hover:bg-terra-dark text-white h-10"
            >
              {placesBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Suche läuft… (bis zu 90 Sek.)</> : <><Search className="h-4 w-4 mr-2"/>Suchen &amp; importieren</>}
            </Button>
          </div>
        </div>

        <p className="text-[12px] text-sand-500 leading-snug">
          Max. 60 Treffer pro Suche · nur Prospects <b>mit gefundener E-Mail</b> werden importiert.
          Kosten pro Suche: ca. 0,05–0,15 € (bei ~30 Treffern). Sinnvoll: pro Bundesland 1× ausführen mit
          verschiedenen Suchbegriffen (&bdquo;ambulanter Pflegedienst&ldquo;, &bdquo;ambulante Pflege&ldquo;, &bdquo;Pflegedienst&ldquo;).
        </p>

        {/* Vollscan-Bereich */}
        <div className="mt-5 pt-5 border-t border-terra/30">
          <div className="flex items-start gap-3 mb-3">
            <Zap className="h-5 w-5 text-terra shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div className="font-semibold text-sand-800 text-[15px]">Vollscan {placesRegion || "(Bundesland wählen)"}</div>
              <div className="text-sand-600 text-[13px] leading-relaxed mt-0.5">
                Klopft <b>alle Städte</b> in {placesRegion || "…"} mit <b>5 Suchbegriff-Varianten</b> ab
                (ambulanter Pflegedienst · Pflegedienst · häusliche Krankenpflege · Hauskrankenpflege · Sozialstation).
                Deduplikation automatisch. Läuft im Browser – lass diesen Tab offen.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            {!batchRunning ? (
              <Button
                onClick={runBatchScan}
                disabled={placesConfigured === false || batchRunning || !CITIES_BY_BUNDESLAND[placesRegion]}
                data-testid="batch-scan-btn"
                className="bg-terra hover:bg-terra-dark text-white h-10"
              >
                <Zap className="h-4 w-4 mr-2"/>Vollscan starten
              </Button>
            ) : (
              <Button
                onClick={stopBatch}
                variant="outline"
                data-testid="batch-stop-btn"
                className="border-red-400 text-red-700 hover:bg-red-50 h-10"
              >
                <StopCircle className="h-4 w-4 mr-2"/>Abbrechen
              </Button>
            )}
            {placesRegion && CITIES_BY_BUNDESLAND[placesRegion] && (
              <span className="text-sand-500 text-[12px]">
                {CITIES_BY_BUNDESLAND[placesRegion].length} Städte × {PFLEGE_KEYWORDS.length} Begriffe ={" "}
                <b>{CITIES_BY_BUNDESLAND[placesRegion].length * PFLEGE_KEYWORDS.length}</b> Suchen ·
                ca. {Math.round(CITIES_BY_BUNDESLAND[placesRegion].length * PFLEGE_KEYWORDS.length * 15 / 60)} Min. ·
                ca. {(CITIES_BY_BUNDESLAND[placesRegion].length * PFLEGE_KEYWORDS.length * 0.05).toFixed(2)} € Google-Kosten
              </span>
            )}
          </div>

          {batchProgress && (
            <div className="mt-4 rounded-lg bg-white border border-cream-300 p-4" data-testid="batch-progress">
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-semibold text-sand-800 text-[14px]">
                  Fortschritt: {batchProgress.done} / {batchProgress.total}
                </div>
                <div className="text-sand-500 text-[12px]">
                  {Math.round((batchProgress.done / batchProgress.total) * 100)}%
                </div>
              </div>
              <div className="w-full bg-cream-200 rounded-full h-2 overflow-hidden mb-3">
                <div
                  className="bg-terra h-2 transition-all"
                  style={{ width: `${Math.min(100, (batchProgress.done / batchProgress.total) * 100)}%` }}
                />
              </div>
              {batchProgress.currentCity && (
                <div className="text-sand-600 text-[12.5px] mb-2">
                  Aktuell: <span className="font-medium">{batchProgress.currentCity}</span>
                </div>
              )}
              <div className="grid grid-cols-4 gap-2 text-[12.5px]">
                <div><span className="text-sand-500">Importiert:</span> <b className="text-terra">{batchProgress.importedTotal}</b></div>
                <div><span className="text-sand-500">Gefunden:</span> <b>{batchProgress.foundTotal}</b></div>
                <div><span className="text-sand-500">Duplikate:</span> {batchProgress.dupTotal}</div>
                <div><span className="text-sand-500">Ohne E-Mail:</span> {batchProgress.noEmailTotal}</div>
              </div>
            </div>
          )}
        </div>

        {placesResult && (
          <div className="mt-4 rounded-lg bg-white border border-cream-300 p-4" data-testid="places-result">
            <div className="text-sand-800 font-semibold text-[14px] mb-2">
              Ergebnis: {placesResult.found} gefunden · <span className="text-terra">{placesResult.imported} neu importiert</span> · {placesResult.duplicates_skipped} Duplikate · {placesResult.no_email_skipped} ohne E-Mail
            </div>
            <details className="text-[13px]">
              <summary className="cursor-pointer text-terra font-medium">Vorschau anzeigen ({placesResult.preview?.length || 0})</summary>
              <div className="mt-2 max-h-64 overflow-y-auto space-y-1">
                {(placesResult.preview || []).map((p, i) => (
                  <div key={i} className={`flex items-center justify-between gap-3 border-b border-cream-200 py-1.5 text-[12.5px] ${p.has_email ? "" : "opacity-50"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sand-800 truncate">{p.firmenname}</div>
                      <div className="text-sand-500 truncate">{p.email || <span className="italic">keine E-Mail gefunden</span>} · {p.ort} · {p.telefon}</div>
                    </div>
                    {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="text-terra text-[11px] hover:underline shrink-0">Web</a>}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>

      {/* CSV Import */}
      <div className="rounded-2xl border border-cream-300 bg-white p-6 mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Upload className="h-5 w-5 text-terra"/>
          <h2 className="font-semibold text-sand-800 text-lg">CSV-Import</h2>
        </div>
        <p className="text-sand-600 text-sm mb-3">
          Format (Semikolon-getrennt, erste Zeile = Header):
          <code className="ml-2 bg-cream-100 px-2 py-0.5 rounded text-[12px]">firmenname;ansprechpartner;email;telefon;ort;bundesland;website;notes</code>
        </p>
        <Textarea
          rows={6}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder={"firmenname;ansprechpartner;email;telefon;ort;bundesland;website;notes\nMusterpflege GmbH;Anna Muster;anna@muster.de;0221 1234567;Köln;Nordrhein-Westfalen;muster.de;Notiz…"}
          data-testid="csv-textarea"
          className="font-mono text-[12.5px]"
        />
        <div className="mt-3 flex gap-3 items-center">
          <Button onClick={handleImport} disabled={importing || !csvText.trim()} data-testid="import-btn" className="bg-terra hover:bg-terra-dark text-white">
            {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Importiere…</> : <><Upload className="h-4 w-4 mr-2"/>Importieren</>}
          </Button>
          <span className="text-sand-500 text-sm">Duplikate (nach E-Mail) werden übersprungen.</span>
        </div>
      </div>

      {/* Prospect List */}
      <div className="rounded-2xl border border-cream-300 bg-white overflow-hidden">
        <div className="p-5 border-b border-cream-200">
          <h2 className="font-semibold text-sand-800 text-lg">Prospects · {prospects.length}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-100 text-sand-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Pflegedienst</th>
                <th className="text-left px-4 py-3">E-Mail</th>
                <th className="text-left px-4 py-3">Ort</th>
                <th className="text-left px-4 py-3">Touches</th>
                <th className="text-left px-4 py-3">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {prospects.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sand-500">Noch keine Prospects importiert.</td></tr>
              )}
              {prospects.map((p) => {
                const next = getNextTouch(p);
                const touchNow = selectedTouch[p.id] ?? next ?? 1;
                return (
                  <tr key={p.id} className="border-t border-cream-200" data-testid={`prospect-row-${p.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-sand-800">{p.firmenname}</div>
                      {p.ansprechpartner && <div className="text-sand-500 text-[12px]">{p.ansprechpartner}</div>}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <a href={`mailto:${p.email}`} className="text-terra hover:underline">{p.email}</a>
                      {p.telefon && <div className="text-sand-500 text-[12px]">{p.telefon}</div>}
                    </td>
                    <td className="px-4 py-3 align-top text-sand-700">{p.ort || "—"}<div className="text-sand-500 text-[12px]">{p.bundesland}</div></td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex gap-1">
                        {[1, 2, 3].map((t) => {
                          const sent = (p.touches_sent || []).find((x) => x.touch === t);
                          return (
                            <span
                              key={t}
                              title={sent ? new Date(sent.sent_at).toLocaleString("de-DE") : "nicht gesendet"}
                              className={`inline-flex items-center justify-center w-6 h-6 rounded text-[11px] font-semibold ${
                                sent ? "bg-terra text-white" : "bg-cream-200 text-sand-400"
                              }`}
                            >
                              {t}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {next ? (
                        <div className="flex items-center gap-2">
                          <select
                            value={touchNow}
                            onChange={(e) => setSelectedTouch((s) => ({ ...s, [p.id]: Number(e.target.value) }))}
                            className="h-8 rounded border border-cream-300 text-[13px] px-2"
                            data-testid={`touch-select-${p.id}`}
                          >
                            {[1, 2, 3].filter((t) => !(p.touches_sent || []).some((x) => x.touch === t))
                              .map((t) => <option key={t} value={t}>Touch {t}</option>)}
                          </select>
                          <Button
                            size="sm"
                            onClick={() => sendTouch(p.id, touchNow)}
                            disabled={sendingId === p.id || p.unsubscribed}
                            data-testid={`send-btn-${p.id}`}
                            className="bg-terra hover:bg-terra-dark text-white h-8 px-3 text-[13px]"
                          >
                            {sendingId === p.id ? <Loader2 className="h-3 w-3 animate-spin"/> : <><Mail className="h-3 w-3 mr-1"/>Senden</>}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-sand-500 text-[13px]">Sequenz abgeschlossen</span>
                      )}
                      {p.unsubscribed && <div className="text-red-500 text-[12px] mt-1">abgemeldet</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-sand-800" }) {
  return (
    <div className="rounded-xl border border-cream-300 bg-white p-4">
      <div className="text-[11px] uppercase tracking-wider text-sand-500 mb-1">{label}</div>
      <div className={`text-2xl font-display font-bold ${accent}`}>{value ?? 0}</div>
    </div>
  );
}
